import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { CstPisCofinsTexto, getCstPisCofinsTexto } from '@shared/constants/motor-fiscal/cst-pis-cofins-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    aplicarObrigatoriedadeIndicadores,
    controlesDasRegrasEntreCampos,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { MaskService } from '@shared/services/mask.service';
import { CstCofinsStruct } from '@shared/services/structs/motor-fiscal/cst-cofins.struct';

/**
 * Campo do indicador do CST do COFINS -> control do grupo `cofins` do item da venda. Os demais controls de uma
 * chave são limpos junto. A tela da venda não tem o valor por unidade da ST (`vAliqProdST`): a ST fica só pela alíquota.
 */
const MAPA_CAMPOS_COFINS_VENDA: MapaCamposIndicador = {
    pCOFINS: ['percentualAliquota', 'valorBaseCalculo', 'valorCofins'],
    vAliqProd: ['valorAliquotaUnidade', 'quantidadeBaseCalculo'],
    pCOFINSST: ['percentualAliquotaSt', 'valorBaseCalculoSt', 'valorCofinsSt', 'quantidadeBaseCalculoSt', 'valorAliquotaUnidadeSt'],
};

/**
 * Aba "COFINS" do modal do item da venda (cópia da aba COFINS do item da NF-e de
 * saída). Mesma mecânica e CSTs do PIS.
 */
@Component({
    selector: 'app-vd-item-aba-cofins',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './vd-item-aba-cofins.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-cofins.component.css'],
})
export class VdItemAbaCofinsComponent implements OnInit, OnChanges, OnDestroy {
    /** O grupo `cofins` do item. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public listaCstCofins: CstCofinsStruct[] = [];
    @Input() public isRecalculoPendente: boolean = false;

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getDynamicCurrencyMask();
    public readonly valorMask = this.maskService.getCurrencyMask(2);
    public readonly decimalMask = this.maskService.getDynamicDecimalMask();

    private readonly destroy$ = new Subject<void>();

    constructor(private maskService: MaskService) {}

    public ngOnInit(): void {
        ['codigoCst', ...controlesDasRegrasEntreCampos(MAPA_CAMPOS_COFINS_VENDA)].forEach((campo) =>
            this.form.get(campo)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.sincronizarObrigatoriedade()));
        this.sincronizarObrigatoriedade();
    }

    /** Os indicadores chegam com a lista de CST (lookup assíncrono). */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['listaCstCofins'] && !changes['listaCstCofins'].firstChange) this.sincronizarObrigatoriedade();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** A venda só usa os CSTs de saída, pelo indicador, da tabela de CST COFINS. */
    public get listaCstCofinsSaida(): CstCofinsStruct[] {
        return this.listaCstCofins.filter((c) => isCodigoDaOperacao(c.indicadores, TipoOperacaoEnum.Saida));
    }

    public get indicadores(): IndicadoresCst {
        const cst = this.form?.get('codigoCst')?.value;

        return lerIndicadoresCst(this.listaCstCofins.find((c) => c.codigo === cst)?.indicadores);
    }

    public get texto(): CstPisCofinsTexto | null {
        const cst = this.form?.get('codigoCst')?.value;

        return getCstPisCofinsTexto(cst, this.listaCstCofins.find((c) => c.codigo === cst)?.descricao, this.indicadores, 'COFINS');
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    /** Troca feita pelo usuário: zera os campos que o novo CST deixou de exibir (COFINSNT não tem valores). */
    public onCstSelecionado(): void {
        limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_COFINS_VENDA);
        this.sincronizarObrigatoriedade();
    }

    /** Mesma regra da natureza: pelo indicador do CST, com a escolha entre a alíquota e o valor por unidade. */
    private sincronizarObrigatoriedade(): void {
        const isComCst = !!this.form.get('codigoCst')?.value;

        aplicarObrigatoriedadeIndicadores(this.form, isComCst ? this.indicadores : null, MAPA_CAMPOS_COFINS_VENDA);
    }
}
